import { Fragment, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { Copy, Info, Play, Share2, Star, X, Eye } from 'lucide-react';
import { TechniqueStatus, type Technique } from '../types';
import { ART_META, techArts } from '../data/arts';
import { getThumbnailUrl, getThumbnailFallbackUrl } from '../utils/video';
import { shareTechnique } from '../utils/share';
import { isMobile } from './PipPlayer';
import { variantSections, type TechniqueGroup } from '../utils/groupTechniques';
import { TYPE_LABELS } from '../constants/layout';
import { TYPE_ICONS } from '../data/icons';

interface VersionPickerProps {
  group: TechniqueGroup;
  statusOf: (id: string) => TechniqueStatus;
  isWatched: (t: Technique) => boolean;
  onPlay: (t: Technique) => void;
  /** Omitted in the Archived view, where starring isn't offered. */
  onToggleMat?: (t: Technique) => void;
  onInfo: (t: Technique) => void;
  onClose: () => void;
}

// The chooser behind a grouped technique card: one row per clip, tap to play.
//
// Rows carry the star / share / details buttons that the collapsed card drops,
// because those are all per-clip actions — a favorite is one particular
// creator's take, not the technique in the abstract. Losing them on the group
// card and regaining them here is what keeps grouping a display change rather
// than a change to what the user can do.
export default function VersionPicker({
  group, statusOf, isWatched, onPlay, onToggleMat, onInfo, onClose,
}: VersionPickerProps) {
  const mobile = isMobile();
  // The clips come pre-sorted by kind of move (groupTechniques), so the runs
  // are already contiguous — this just cuts them apart for the headings.
  const sections = variantSections(group.variants);

  // The row this picker is about stays lit: the backdrop gets a hole cut
  // where that row sits (.technique-card--picking, set by the list), so the
  // move being chosen between is readable while everything else dims. A hole
  // rather than a z-index lift, because the list scrolls inside its own layer
  // and nothing in it can paint above the backdrop.
  const backdrop = useRef<HTMLDivElement>(null);
  const [hole, setHole] = useState<CSSProperties | undefined>();
  useLayoutEffect(() => {
    const row = document.querySelector('.technique-card--picking');
    const bd = backdrop.current;
    if (!row || !bd) { setHole(undefined); return; }
    const r = row.getBoundingClientRect();
    const b = bd.getBoundingClientRect();
    const pad = 3;
    const l = r.left - b.left - pad, t = r.top - b.top - pad, rr = r.right - b.left + pad, bt = r.bottom - b.top + pad;
    setHole({
      clipPath: `polygon(evenodd, 0 0, 100% 0, 100% 100%, 0 100%, 0 0, ${l}px ${t}px, ${l}px ${bt}px, ${rr}px ${bt}px, ${rr}px ${t}px, ${l}px ${t}px)`,
    });
  }, [group]);

  return (
    <div className="version-picker-backdrop" ref={backdrop} style={hole} onClick={onClose}>
      <div className="version-picker" onClick={(e) => e.stopPropagation()}>
        <div className="version-picker-header">
          <span className="version-picker-title">
            {group.name}
            <span className="version-picker-count">{group.variants.length} versions</span>
          </span>
          <button className="version-picker-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="version-picker-list">
          {sections.map(({ type, items }) => (
            <Fragment key={type}>
              {/* One kind of move in the stack needs no heading — the header
                  already says what this is. Several, and each run gets its
                  own: submissions, then the escapes, then the transitions. */}
              {sections.length > 1 && (
                <div className="version-section">
                  {(() => { const Icon = TYPE_ICONS[type]; return Icon ? <Icon size={12} /> : null; })()}
                  {TYPE_LABELS[type] ?? type}
                  <span className="version-section-count">{items.length}</span>
                </div>
              )}
              {items.map((t) => {
                const src = getThumbnailUrl(t.instagramUrl, t.thumbnail);
                const watched = isWatched(t);
                const starred = statusOf(t.id) === TechniqueStatus.MarkedForPractice;
                const credit = t.attribution || (t.author ? `@${t.author}` : 'Unknown creator');
                return (
                  <div
                    key={t.id}
                    className={`version-row${watched ? ' version-row--watched' : ''}`}
                    role="button"
                    tabIndex={0}
                    title={t.instagramUrl ? 'Watch this version' : credit}
                    onClick={() => onPlay(t)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPlay(t); } }}
                  >
                    <span className="version-row-thumb">
                      {src ? (
                        <img
                          src={src}
                          alt=""
                          loading="lazy"
                          decoding="async"
                          onError={(e) => {
                            const next = getThumbnailFallbackUrl(e.currentTarget.src);
                            if (next) e.currentTarget.src = next; else e.currentTarget.remove();
                          }}
                        />
                      ) : null}
                      <span className="version-row-glyph">
                        {watched ? <Eye size={13} /> : <Play size={13} fill="currentColor" />}
                      </span>
                    </span>

                    <span className="version-row-info">
                      <span className="version-row-credit">{credit}</span>
                      {/* What separates one version from another: the sport it's
                          from, and the entry it's taught off. Level and gi/no-gi
                          used to sit here and said less about which clip to
                          watch. */}
                      <span className="version-row-chips">
                        {techArts(t).map((a) => (
                          <span key={a} className="version-row-chip">{ART_META[a].shortLabel}</span>
                        ))}
                        {t.context && <span className="version-row-chip version-row-chip--context">{t.context}</span>}
                      </span>
                    </span>

                    <span className="version-row-actions" onClick={(e) => e.stopPropagation()}>
                      {onToggleMat && (
                        <button
                          className={`video-link video-link--star${starred ? ' video-link--starred' : ''}`}
                          onClick={() => onToggleMat(t)}
                          aria-label={starred ? 'Remove from Favorites' : 'Add to Favorites'}
                          title={starred ? 'In favorites: tap to remove' : 'Add to Favorites'}
                        >
                          <Star size={12} fill={starred ? 'currentColor' : 'none'} />
                        </button>
                      )}
                      <button
                        className="video-link video-link--share"
                        onClick={() => shareTechnique(t.id, t.name, mobile)}
                        aria-label={mobile ? 'Share this version' : 'Copy link to this version'}
                        title={mobile ? 'Share this version' : 'Copy link to this version'}
                      >
                        {mobile ? <Share2 size={12} /> : <Copy size={12} />}
                      </button>
                      <button
                        className="video-link video-link--info"
                        onClick={() => onInfo(t)}
                        aria-label="Technique details"
                        title="Technique details"
                      >
                        <Info size={12} />
                      </button>
                    </span>
                  </div>
                );
              })}
            </Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}
